import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Truck, Eye, EyeOff, ArrowLeft, Camera, IdCard, Upload } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { registerWithDocuments } = useAuth();
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const selfieInputRef = useRef(null);
  const idInputRef = useRef(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    paypal_email: '',
    phone: '',
    address: '',
    ssn_ein: '',
    agree_terms: false,
    agree_contractor: false,
    selfie: null,
    id_document: null
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [type]: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'selfie') {
          setSelfiePreview(reader.result);
        } else {
          setIdPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth_passwords_not_match'));
      return;
    }

    if (!formData.agree_terms) {
      toast.error(t('auth_agree_terms_required'));
      return;
    }

    if (!formData.agree_contractor) {
      toast.error(t('auth_contractor_required'));
      return;
    }

    if (!formData.selfie) {
      toast.error('Please upload a selfie photo');
      return;
    }

    if (!formData.id_document) {
      toast.error('Please upload a photo of your ID');
      return;
    }

    setLoading(true);
    try {
      await registerWithDocuments(formData);
      toast.success(t('auth_register_success'));
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth_register_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 mb-6 hover:text-gray-900"
          data-testid="back-to-app-btn"
        >
          <ArrowLeft size={20} />
          {t('auth_back_to_app')}
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Truck size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="register-title">{t('auth_create_account')}</h1>
          <p className="text-gray-500 mt-2">{t('auth_join')}</p>
        </div>

        <Card className="p-6 border-0 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">{t('auth_full_name')} *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="John Doe"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">{t('auth_email_address')} *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="paypal_email">{t('auth_paypal_email')} *</Label>
              <Input
                id="paypal_email"
                name="paypal_email"
                type="email"
                value={formData.paypal_email}
                onChange={handleChange}
                required
                placeholder="john@paypal.com"
                data-testid="register-paypal-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t('auth_paypal_note')}</p>
            </div>

            <div>
              <Label htmlFor="phone">{t('auth_phone')} *</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(555) 123-4567"
                data-testid="register-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="address">{t('auth_address')} *</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                placeholder="123 Main St, City, State, ZIP"
                data-testid="register-address-input"
              />
            </div>

            <div>
              <Label htmlFor="ssn_ein">{t('auth_ssn_ein')} *</Label>
              <Input
                id="ssn_ein"
                name="ssn_ein"
                value={formData.ssn_ein}
                onChange={handleChange}
                required
                placeholder="XXX-XX-XXXX"
                data-testid="register-ssn-input"
              />
              <p className="text-xs text-gray-500 mt-1">{t('auth_ssn_note')}</p>
            </div>

            {/* Selfie Upload */}
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 mb-2">
                <Camera size={18} className="text-orange-500" />
                Selfie Photo *
              </Label>
              <input
                ref={selfieInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={(e) => handleFileChange(e, 'selfie')}
                className="hidden"
                data-testid="selfie-input"
              />
              <div 
                onClick={() => selfieInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-4 cursor-pointer hover:border-orange-400 transition-colors"
              >
                {selfiePreview ? (
                  <div className="relative">
                    <img src={selfiePreview} alt="Selfie preview" className="w-full h-32 object-cover rounded-lg" />
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-sm">Click to change</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <Camera size={32} />
                    <span className="text-sm">Take or upload a selfie</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Clear photo of your face for identity verification</p>
            </div>

            {/* ID Document Upload */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <IdCard size={18} className="text-orange-500" />
                Government ID *
              </Label>
              <input
                ref={idInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileChange(e, 'id_document')}
                className="hidden"
                data-testid="id-document-input"
              />
              <div 
                onClick={() => idInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-xl p-4 cursor-pointer hover:border-orange-400 transition-colors"
              >
                {idPreview ? (
                  <div className="relative">
                    <img src={idPreview} alt="ID preview" className="w-full h-32 object-cover rounded-lg" />
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white text-sm">Click to change</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-gray-500">
                    <IdCard size={32} />
                    <span className="text-sm">Upload photo of your ID</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Driver's license, passport, or state ID</p>
            </div>

            <div className="relative">
              <Label htmlFor="password">{t('auth_password')} *</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="••••••••"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">{t('auth_confirm_password')} *</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="••••••••"
                data-testid="register-confirm-password-input"
              />
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree_terms"
                  name="agree_terms"
                  checked={formData.agree_terms}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agree_terms: checked }))}
                  data-testid="agree-terms-checkbox"
                />
                <label htmlFor="agree_terms" className="text-sm text-gray-600 leading-tight cursor-pointer">
                  {t('auth_agree_terms')} <span className="text-orange-600 underline">{t('auth_terms_of_service')}</span> {t('auth_and')} <span className="text-orange-600 underline">{t('auth_privacy_policy')}</span> *
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="agree_contractor"
                  name="agree_contractor"
                  checked={formData.agree_contractor}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agree_contractor: checked }))}
                  data-testid="agree-contractor-checkbox"
                />
                <label htmlFor="agree_contractor" className="text-sm text-gray-600 leading-tight cursor-pointer">
                  {t('auth_contractor_acknowledge')} <strong>{t('auth_independent_contractor')}</strong> {t('auth_not_employee')} *
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-6"
            >
              {loading ? t('auth_creating_account') : t('auth_create_account')}
            </Button>
          </form>
        </Card>

        <p className="text-center mt-6 text-gray-600">
          {t('auth_already_have_account')}{' '}
          <Link to="/login" className="text-orange-600 font-medium hover:underline" data-testid="signin-link">
            {t('auth_sign_in')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
